import { Grid } from "@material-ui/core";
import { SarsModel } from "../../domain/SarsModel";
import { SectionProps } from "./SectionProps";
import { Controller } from "react-hook-form";
import { fieldLabels } from "./fieldLabels";
import { Error } from "../common/Error";
import { useLookup } from "@ofqual/portal-core/core/hooks";

import { Select } from "@ofqual/portal-components/components/Select";
import { DatePicker } from "@ofqual/portal-components/components/DatePicker";
import { MarkdownEditor } from "@ofqual/portal-components/components/MarkdownEditor";
import { useDispatch, useSelector } from "react-redux";
import { authSelector } from "../../redux/selectors";
import {
  sarsContactMethodService,
  sarsTypeService,
  sarsCategoryService,
  sarsService,
} from "../../services";
import { FileInput } from "@ofqual/portal-components/components/FileInput";
import { AttachmentList } from "../common/AttachmentList";
import { Attachment } from "../../domain/Attachment";
import { deleteSelectedAttachment } from "../../redux/actions";

export const CaseDetails: React.FC<{
  sectionProps: SectionProps<SarsModel>;
}> = ({ sectionProps }) => {
  const { control, errors, disabled, isCreateMode, item } = sectionProps;

  const [
    contactMethodLookups,
    typeMethodLookups,
    sarsCategoryLookups,
  ] = useLookup(
    sarsContactMethodService,
    sarsTypeService,
    sarsCategoryService
  );

  const dispatch = useDispatch();

  const { token } = useSelector(authSelector);

  const deleteAttachment = (attachment: Attachment) =>
    dispatch(deleteSelectedAttachment(attachment));

  const buildGetBlob = (blobId: string): Promise<Blob> =>
    sarsService.downloadAttachment(item!.id, blobId, { token });

  return (
    <Grid container spacing={3}>
      {isCreateMode && (
        <Grid item xs={12} sm={6}>
          <Controller
            control={control}
            rules={{ required: true }}
            name="dateReceived"
            as={
              <DatePicker
                label={fieldLabels.dateReceived}
                dataTest="sar-dateReceived-field"
                readOnly={disabled}
                required
                clearable
              />
            }
          />
          {errors.dateReceived?.type === "required" && (
            <Error data-test-dataReceived="dateReceived-validation">
              This field is required
            </Error>
          )}
        </Grid>
      )}
      {!isCreateMode && (
        <Grid item xs={12} sm={6}>
          <Controller
            control={control}
            name="categoryId"
            as={
              <Select
                label={fieldLabels.category}
                data-test="category-field"
                readOnly={disabled}
              >
                {sarsCategoryLookups}
              </Select>
            }
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6}>
        <Controller
          control={control}
          rules={{ required: true }}
          name="sourceId"
          as={
            <Select
              label={fieldLabels.sourceMethod}
              data-test-source="source-field"
              readOnly={disabled}
              required
            >
              {contactMethodLookups}
            </Select>
          }
        />
        {errors.sourceId?.type === "required" && (
          <Error data-test="source-validation">This field is required</Error>
        )}
      </Grid>
      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{
            validate: (value) => {
              if (value === undefined || value.length === 0) return false;
              else return true;
            },
          }}
          name="requestType"
          as={
            <Select
              label={fieldLabels.requestType}
              data-test="sars-request-type"
              readOnly={disabled}
              selectVariant="multiple"
              required
            >
              {typeMethodLookups}
            </Select>
          }
        />
        {errors.requestType && (
          <Error data-test="requestType-validation">
            This field is required
          </Error>
        )}
      </Grid>

      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{ required: true, maxLength: 10000 }}
          name="requestDetail"
          render={({ onChange, value }) => (
            <>
              <label>{fieldLabels.requestDetails} *</label>
              <MarkdownEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
                dataTest="sars-requestDetail"
              />
            </>
          )}
        />
        {errors.requestDetail?.type === "required" && (
          <Error data-test="requestDetail-validation">
            This field is required
          </Error>
        )}
        {errors.requestDetail?.type === "maxLength" && (
          <Error data-test="requestDetail-length-validation">
            Max length exceeded
          </Error>
        )}
      </Grid>
      <Grid item xs={12} sm={12}>
        <Controller
          control={control}
          rules={{ maxLength: 5000 }}
          name="caseSummary"
          render={({ onChange, value }) => (
            <>
              <label>{fieldLabels.summary}</label>
              <MarkdownEditor
                onChange={onChange}
                value={value}
                readOnly={disabled}
                dataTest="sars-caseSummary"
              />
            </>
          )}
        />
        {errors.caseSummary?.type === "maxLength" && (
          <Error data-test="summary-length-validation">
            Max length exceeded
          </Error>
        )}
      </Grid>
      {!isCreateMode && (
        <>
          <Grid item xs={12}>
            <Controller
              control={control}
              name="files"
              defaultValue={[]}
              as={
                <FileInput
                  multiple={true}
                  dataTest="select-files-button"
                  disabled={disabled}
                />
              }
            />
          </Grid>
          {item?.sarsAttachments ? (
            <AttachmentList
              canEdit={!disabled}
              attachments={item.sarsAttachments}
              deleteAttachment={deleteAttachment}
              getBlob={buildGetBlob}
            />
          ) : null}
        </>
      )}
    </Grid>
  );
};
